import {
  Plane, Hotel, Car, Utensils, Lightbulb, MapPin,
  User as UserIcon, Calendar, Trash2, Pencil,
} from "lucide-react";
import { DASH, DOT, tripDates, translateOr } from "./format";

export default function TripCard({ trip, t, language, onEdit, onDelete }) {
  const transportLabel = translateOr(
    t,
    `trasferte.transport.${trip.transport_type}`,
    trip.transport_type
  );
  const when = tripDates(trip, language);
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow relative" data-testid={`trip-card-${trip.id}`}>
      {/* Correcting comes before deleting, and is the one that gets used: a
          hotel name typed wrong or a restaurant remembered later used to mean
          deleting the record and retyping it from memory, which is how the
          hotel somebody wanted to remember gets lost. */}
      <div className="absolute top-3 right-3 flex items-center gap-0.5">
        <button
          type="button"
          data-testid={`trip-edit-btn-${trip.id}`}
          onClick={onEdit}
          className="p-1.5 rounded-md text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
          aria-label={t("trasferte.editTrip")}
          title={t("trasferte.editTrip")}
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          type="button"
          data-testid={`trip-delete-btn-${trip.id}`}
          onClick={onDelete}
          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          aria-label={t("trasferte.deleteTripAria")}
          title={t("trasferte.deleteTrip")}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* pr-16 rather than pr-9: two buttons sit in that corner now, and a long
          name would run under them. */}
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-slate-900 font-semibold min-w-0">
            <UserIcon className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="break-words">{trip.created_by_name}</span>
          </div>
          {/* Only on a trip with no client: with one, the place is the
              client's and is already at the top of the page. */}
          {!trip.client_id && trip.city && (
            <div className="flex items-center gap-2 text-sm text-slate-500 mt-0.5">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="break-words">{trip.city}</span>
            </div>
          )}
        </div>
        {/* Nothing at all when the dates were left empty, rather than the day
            the record happened to be typed in. */}
        {when && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400 shrink-0">
            <Calendar className="w-3 h-3" />
            {when}
          </div>
        )}
      </div>

      <div className="space-y-2 text-sm">
        {trip.airport && (
          <Row icon={Plane} label={t("trasferte.airport")}>
            <span className="text-slate-700">{trip.airport}</span>
          </Row>
        )}

        {trip.hotels && trip.hotels.length > 0 && (
          <Row icon={Hotel} label={t("trasferte.hotels")}>
            <span className="text-slate-700">{trip.hotels.join(` ${DOT} `)}</span>
          </Row>
        )}

        {trip.transport_type && (
          <Row icon={Car} label={t("trasferte.transport.label")}>
            <span className="text-slate-700">{transportLabel}</span>
            {trip.transport_note && (
              <span className="text-slate-400"> {DASH} {trip.transport_note}</span>
            )}
          </Row>
        )}

        {trip.restaurants && trip.restaurants.length > 0 && (
          <Row icon={Utensils} label={t("trasferte.restaurants")}>
            <ul className="text-slate-700 space-y-0.5">
              {trip.restaurants.map((r, idx) => (
                <li key={idx}>
                  <span className="font-medium">{r.name}</span>
                  {r.note && <span className="text-slate-400"> {DASH} {r.note}</span>}
                </li>
              ))}
            </ul>
          </Row>
        )}

        {trip.notes && (
          <Row icon={Lightbulb} label={t("trasferte.tips")}>
            <span className="text-slate-700 whitespace-pre-wrap">{trip.notes}</span>
          </Row>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-5 mt-0.5 shrink-0 text-slate-400">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        {/* Free text: a pasted address or a restaurant note is one long word as
            far as the layout is concerned, and would stretch the card. */}
        <div className="mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}
